'use client';

import { useEffect } from 'react';
import { useT } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useT();
  const { trackErrorRetry } = useAnalytics();

  useEffect(() => {
    try {
      const payload = {
        type: 'react_error',
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      };
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/errors',
          new Blob([JSON.stringify(payload)], { type: 'application/json' }),
        );
      } else {
        fetch('/api/errors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }
    } catch {
      // Reporting must never throw
    }
  }, [error]);

  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center space-y-4 p-8">
        <h2 className="text-xl font-bold">{t('errors.somethingWentWrong')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('errors.pageLoadFailed')}
        </p>
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => {
              trackErrorRetry('error');
              reset();
            }}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
          >
            {t('errors.tryAgain')}
          </button>
          {/*
            Take-me-home button — for authed users this lands them in
            the workspace; for anonymous visitors it bounces through
            /workspace which forwards to /login?returnTo=/workspace.
            Either way the user gets out of the broken view without
            another click.
          */}
          <a
            href="/workspace"
            className="px-4 py-2 text-sm border border-border text-foreground rounded-md hover:bg-accent transition-colors"
          >
            Workspace
          </a>
        </div>
      </div>
    </div>
  );
}
